import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Plus, X, Check, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * MissingDocsPicker — chip-based multi-select tied to the shared
 * `missing_docs_catalog` master list. Officer clicks "+" to open a searchable
 * popover of catalog items. Picking one adds it as a chip. The free-text
 * "Other" row lets them add anything not in the list and (optionally) persist
 * it back to the catalog so teammates see it next time.
 */
export default function MissingDocsPicker({ value = [], onChange, allowCatalogEdit = true }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [custom, setCustom] = useState("");
  const [saveToCatalog, setSaveToCatalog] = useState(true);
  const inputRef = useRef();

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/missing-docs-catalog");
      setCatalog(r.data || []);
    } catch (e) { /* soft fail — chips still work */ }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const selectedSet = useMemo(() => new Set((value || []).map(v => v.toLowerCase())), [value]);
  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog
      .filter(c => !selectedSet.has(c.label.toLowerCase()))
      .filter(c => !q || c.label.toLowerCase().includes(q));
  }, [catalog, selectedSet, search]);

  const add = (label) => {
    const clean = (label || "").trim();
    if (!clean) return;
    if (selectedSet.has(clean.toLowerCase())) return;
    onChange?.([...(value || []), clean]);
  };
  const remove = (label) => {
    onChange?.((value || []).filter(v => v !== label));
  };

  const addCustom = async () => {
    const clean = custom.trim();
    if (!clean) return;
    add(clean);
    setCustom("");
    if (saveToCatalog && allowCatalogEdit) {
      try {
        await api.post("/missing-docs-catalog", { label: clean });
        reload();
      } catch (e) { toast.error("Added to list but could not save to catalog"); }
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const removeCatalogItem = async (item) => {
    if (!window.confirm(`Remove "${item.label}" from the shared catalog?`)) return;
    try {
      await api.delete(`/missing-docs-catalog/${item.id}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div data-testid="missing-docs-picker" className="flex flex-wrap items-center gap-1.5 min-h-[38px] px-2 py-1.5 rounded-md border border-slate-200 bg-white">
      {(value || []).map(label => (
        <span key={label} data-testid={`missing-chip-${label}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-900 text-xs">
          {label}
          <button type="button" onClick={() => remove(label)} className="rounded-full hover:bg-amber-200 p-0.5" aria-label={`Remove ${label}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" data-testid="missing-docs-add-btn" className="h-7 px-2 border-dashed">
            <Plus className="w-3.5 h-3.5 mr-1" />Add
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-2 border-b border-slate-200">
            <Input
              data-testid="missing-docs-search"
              placeholder="Search documents…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {loading && <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>}
            {!loading && available.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-500">No matching documents.</div>
            )}
            {available.map(item => (
              <div key={item.id} className="group flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50">
                <button
                  type="button"
                  data-testid={`missing-docs-pick-${item.label}`}
                  onClick={() => { add(item.label); setSearch(""); }}
                  className="flex items-center gap-2 text-sm text-slate-800 flex-1 text-left"
                >
                  <Check className="w-3.5 h-3.5 text-emerald-500 opacity-0 group-hover:opacity-100" />
                  {item.label}
                </button>
                {allowCatalogEdit && (
                  <button
                    type="button"
                    onClick={() => removeCatalogItem(item)}
                    className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 p-1"
                    title="Remove from shared catalog"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 p-2 space-y-2 bg-slate-50">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 flex items-center gap-1">
              <Pencil className="w-3 h-3" />Other / Custom
            </div>
            <div className="flex items-center gap-1">
              <Input
                ref={inputRef}
                data-testid="missing-docs-custom-input"
                placeholder="Type anything…"
                value={custom}
                onChange={e => setCustom(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                className="h-8 text-sm bg-white"
              />
              <Button
                type="button"
                size="sm"
                data-testid="missing-docs-custom-add"
                onClick={addCustom}
                disabled={!custom.trim()}
                className="h-8 px-2 bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            {allowCatalogEdit && (
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="missing-docs-save-to-catalog"
                  checked={saveToCatalog}
                  onChange={e => setSaveToCatalog(e.target.checked)}
                  className="rounded"
                />
                Also save to the shared list so teammates can pick it next time
              </label>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
